import React from 'react';
import { HiOutlineLightningBolt, HiOutlineHand } from 'react-icons/hi';
import BadgeStatus from '../../../../atoms/BadgeStatus';
import MenuButton from '../../../../atoms/MenuButton';
import { Group, MessageComposition, Routing } from '../../../../../types/communication';
import { ChannelTile } from '../shared/channelVisuals';

interface MessageCompositionRowProps {
    meta: MessageComposition;
    striped: boolean;
    group: Group | undefined;
    variableCount: number;
    triggers: Routing[];
    onOpen: () => void;
    onAction: (action: 'open' | 'edit' | 'duplicate' | 'toggle') => void;
}

const relative = (iso?: string | null): string => {
    if (!iso) return '—';
    const time = new Date(iso).getTime();
    if (Number.isNaN(time)) return '—';
    const diff = Date.now() - time;
    const minutes = Math.round(diff / 60000);
    if (minutes < 1) return 'agora';
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `há ${hours} h`;
    const days = Math.round(hours / 24);
    if (days < 30) return `há ${days} dia${days > 1 ? 's' : ''}`;
    return new Date(iso).toLocaleDateString('pt-BR');
};

const absolute = (iso?: string | null): string | undefined => {
    if (!iso) return undefined;
    const time = new Date(iso).getTime();
    return Number.isNaN(time) ? undefined : new Date(iso).toLocaleString('pt-BR');
};

const MessageCompositionRow: React.FC<MessageCompositionRowProps> = ({ meta, striped, group, variableCount, triggers, onOpen, onAction }) => {
    const active = meta.status === 'ACTIVE';
    return (
        <tr
            onClick={onOpen}
            className={`${striped ? 'bg-slate-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-900'} border-b border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-teal-50/60 dark:hover:bg-slate-700`}
        >
            <td className="py-2 px-2 max-w-0 w-full">
                <div className="flex items-center gap-3 min-w-0">
                    <ChannelTile channel={meta.channel} />
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={meta.description}>
                            {meta.description || meta.name}
                        </div>
                        <div className="text-[11px] font-mono text-gray-400 truncate">{meta.name}</div>
                    </div>
                </div>
            </td>
            <td className="py-2 px-2">
                {group || meta.groupName ? (
                    <span className="inline-block max-w-[120px] truncate rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200" title={group?.name ?? meta.groupName ?? ''}>{group?.name ?? meta.groupName}</span>
                ) : (
                    <span className="text-xs text-gray-400">—</span>
                )}
            </td>
            <td className="py-2 px-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {(() => {
                    const templateCount = meta.templateCount ?? meta.templateIds.length;
                    const varCount = meta.variableCount ?? variableCount;
                    return `${templateCount} template${templateCount === 1 ? '' : 's'} · ${varCount} var.`;
                })()}
            </td>
            <td className="py-2 px-2">
                {triggers.length ? (
                    <span
                        className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 max-w-[180px]"
                        title={triggers.map((t) => t.event).join('\n')}
                    >
                        <HiOutlineLightningBolt size={14} className="shrink-0" />
                        <span className="truncate">{triggers.length === 1 ? triggers[0].event : `${triggers.length} eventos`}</span>
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                        <HiOutlineHand size={14} /> Manual
                    </span>
                )}
            </td>
            <td className="py-2 px-2 text-xs text-gray-500 whitespace-nowrap" title={absolute(meta.updatedAt)}>
                {relative(meta.updatedAt)}
            </td>
            <td className="py-2 px-2">
                <BadgeStatus color={active ? 'green' : 'red'} text={active ? 'Ativo' : 'Inativo'} />
            </td>
            <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                <MenuButton
                    ariaLabel="Ações da composição"
                    options={[
                        { key: 'open', label: 'Abrir', type: 'item' },
                        { key: 'edit', label: 'Editar', type: 'item' },
                        { key: 'duplicate', label: 'Duplicar', type: 'item' },
                        { key: 'sep', type: 'separator' },
                        { key: 'toggle', label: active ? 'Desativar…' : 'Ativar…', type: 'item', backgroundColor: active ? '#DC2626' : '#16A34A' },
                    ]}
                    onSelect={(key) => onAction(key as 'open' | 'edit' | 'duplicate' | 'toggle')}
                />
            </td>
        </tr>
    );
};

export default MessageCompositionRow;
